/** Input types whose caret can be read and set (`selectionStart` is null on the rest). */
const CARET_INPUT_TYPES = new Set(['text', 'search', 'url', 'tel', 'password']);

/**
 * Whether keys typed at `target` belong to a field being typed in: a text
 * input, a textarea or a rich editor. Keyboard shortcuts of a grid or sheet
 * leave those keys to the field.
 */
export function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement)
    return CARET_INPUT_TYPES.has(target.type) || target.type === 'number';
  return (
    target.isContentEditable ||
    target.closest('[contenteditable="true"]') !== null
  );
}

type LineEdgeKey = Pick<
  KeyboardEvent,
  | 'key'
  | 'shiftKey'
  | 'metaKey'
  | 'ctrlKey'
  | 'altKey'
  | 'target'
  | 'defaultPrevented'
  | 'preventDefault'
>;

/**
 * Home and End move the caret of a single-line text field to its start and
 * end (Shift extends the selection), as they do on Windows and in a
 * spreadsheet's cell. macOS instead scrolls the page when the field sits in a
 * scrolling pane, so they seem to do nothing there.
 */
export function moveCaretToLineEdge(event: LineEdgeKey): void {
  const { target } = event;
  if (event.key !== 'Home' && event.key !== 'End') return;
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey)
    return;
  if (!(target instanceof HTMLInputElement)) return;
  if (!CARET_INPUT_TYPES.has(target.type)) return;
  const edge = event.key === 'Home' ? 0 : target.value.length;
  event.preventDefault();
  if (!event.shiftKey) {
    target.setSelectionRange(edge, edge);
    return;
  }
  const anchor =
    target.selectionDirection === 'backward'
      ? (target.selectionEnd ?? edge)
      : (target.selectionStart ?? edge);
  if (edge < anchor) target.setSelectionRange(edge, anchor, 'backward');
  else target.setSelectionRange(anchor, edge, 'forward');
}
